import { t } from '../../i18n';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useVisualViewportBox, useBodyScrollLock } from './WindowModal';

/**
 * SmartComboBox - Dropdown อัจฉริยะที่รองรับข้อมูลจำนวนมาก
 *
 * Features:
 * - ค้นหาได้ (Search/Filter)
 * - รองรับข้อมูลจำนวนมาก (Virtual Scrolling)
 * - Portal Rendering (ไม่ถูกบังโดย overflow:hidden หรือ modal)
 * - Async Loading
 * - Custom Option Rendering
 * - Multi-select Support
 * - Keyboard Navigation
 *
 * @example
 * <SmartComboBox
 *   options={students}
 *   value={selectedStudent}
 *   onChange={setSelectedStudent}
 *   labelKey="name"
 *   valueKey="id"
 *   searchable
 *   placeholder="เลือกนักเรียน"
 * />
 */

const SmartComboBox = ({
  options = [],
  value,
  onChange,
  labelKey = 'label',
  valueKey = 'value',
  placeholder = t('เลือก...'),
  searchable = true,
  searchPlaceholder = t('พิมพ์เพื่อค้นหา...'),
  disabled = false,
  loading = false,
  error = '',
  clearable = true,
  multiple = false,
  maxDisplayItems = 100,
  maxHeight = 280,
  itemHeight = 40,
  renderOption,
  renderValue,
  onSearch,
  noOptionsMessage = t('ไม่พบข้อมูล'),
  loadingMessage = t('กำลังโหลด...'),
  className = '',
  dropdownClassName = '',
  required = false,
  name = '',
  id = '',
  ariaLabel,
  title, // หัวแผ่นเลือกบนมือถือ (ชื่อช่อง เช่น "วิชา") · ไม่ส่ง = ใช้ ariaLabel/placeholder
  allLabel, // มีค่า = แผ่นบนมือถือมีแถวบนสุดสำหรับ "ไม่กรอง" (เช่น "ทุกวิชา") แตะแล้วล้างค่า
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  // มือถือ (<640px): เปิดเป็นแผ่นล่างเต็มความกว้าง รายการสูง 48px แตะง่าย ค้นหาเหนือแป้นพิมพ์
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 639px)').matches : false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 639px)');
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn));
  }, []);
  const sheet = isOpen && isMobile;
  const vvBox = useVisualViewportBox(sheet);
  useBodyScrollLock(sheet);
  const listboxId = useRef(`combobox-list-${Math.random().toString(36).slice(2, 9)}`).current;

  // Get label from option
  const getLabel = useCallback((option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if (typeof labelKey === 'function') return labelKey(option);
    return option[labelKey] || '';
  }, [labelKey]);

  // Get value from option
  const getValue = useCallback((option) => {
    if (!option) return null;
    if (typeof option === 'string') return option;
    if (typeof valueKey === 'function') return valueKey(option);
    return option[valueKey] ?? option;
  }, [valueKey]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;

    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = getLabel(option);
      return label.toLowerCase().includes(lowerSearch);
    });
  }, [options, searchTerm, getLabel]);

  // Display options (limited for performance)
  const displayOptions = useMemo(() => {
    return filteredOptions.slice(0, maxDisplayItems);
  }, [filteredOptions, maxDisplayItems]);

  // Find selected option(s)
  const selectedOptions = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return options.filter(opt => values.includes(getValue(opt)));
    }
    return options.find(opt => getValue(opt) === value);
  }, [value, options, getValue, multiple]);

  // Update dropdown position
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Decide whether to show above or below
    const showAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow;

    const width = Math.min(rect.width, window.innerWidth - 16);

    setDropdownPosition({
      top: showAbove ? rect.top - maxHeight - 4 : rect.bottom + 4,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      width,
      showAbove
    });
  }, [maxHeight]);

  // Handle open/close
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      // Focus search input
      // มือถือ + รายการสั้น: ไม่เด้งแป้นพิมพ์ทันที (จะบังรายการ) ให้แตะช่องค้นหาเองถ้าต้องการ
      if (!(isMobile && options.length <= 12)) {
        setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 10);
      }
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, updatePosition]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < displayOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleSelect(displayOptions[highlightedIndex]);
        } else {
          setIsOpen(true);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // ปิดแค่รายการ ไม่ให้ Esc ทะลุไปปิด WindowModal
        if (isOpen) e.stopPropagation();
        setIsOpen(false);
        setSearchTerm('');
        break;

      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;

      default:
        break;
    }
    // handleSelect ประกาศทีหลัง ใส่ใน deps ไม่ได้ (TDZ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, isOpen, displayOptions, highlightedIndex]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle option select
  const handleSelect = useCallback((option) => {
    if (multiple) {
      const optValue = getValue(option);
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optValue)
        ? currentValues.filter(v => v !== optValue)
        : [...currentValues, optValue];
      onChange?.(newValues);
    } else {
      onChange?.(getValue(option));
      setIsOpen(false);
      setSearchTerm('');
    }
  }, [multiple, value, getValue, onChange]);

  // Handle clear
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : null);
    setSearchTerm('');
  }, [multiple, onChange]);

  // Handle remove tag (for multi-select)
  const handleRemoveTag = useCallback((e, optValue) => {
    e.stopPropagation();
    const currentValues = Array.isArray(value) ? value : [];
    onChange?.(currentValues.filter(v => v !== optValue));
  }, [value, onChange]);

  // Handle search
  const handleSearchChange = useCallback((e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(-1);
    onSearch?.(newValue);
  }, [onSearch]);

  // Render display value
  const renderDisplayValue = () => {
    if (multiple) {
      const selected = selectedOptions || [];
      if (selected.length === 0) {
        return <span className="text-gray-400">{placeholder}</span>;
      }

      return (
        <div className="flex flex-wrap gap-1 py-0.5">
          {selected.slice(0, 3).map((opt, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-100 text-brand-800 text-sm rounded"
            >
              {getLabel(opt)}
              <button
                type="button"
                onClick={(e) => handleRemoveTag(e, getValue(opt))}
                aria-label={t('เอาออก')}
                className="-my-1 -mr-1 flex min-h-[32px] min-w-[32px] items-center justify-center hover:text-brand-600"
              >
                <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          {selected.length > 3 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded">
              +{selected.length - 3}
            </span>
          )}
        </div>
      );
    }

    if (renderValue && selectedOptions) {
      return renderValue(selectedOptions);
    }

    if (selectedOptions) {
      return <span className="text-gray-900">{getLabel(selectedOptions)}</span>;
    }

    return <span className="text-gray-400">{placeholder}</span>;
  };

  // Dropdown content
  const closeSheet = () => { setIsOpen(false); setSearchTerm(''); };
  const sheetMax = vvBox ? Math.round(vvBox.height * 0.85) : Math.round(window.innerHeight * 0.75);
  const dropdownContent = (
    <div
      ref={dropdownRef}
      role={sheet ? 'dialog' : undefined}
      aria-modal={sheet ? 'true' : undefined}
      aria-label={sheet ? (ariaLabel || placeholder) : undefined}
      className={sheet
        ? `fixed inset-x-0 z-[9999] flex flex-col rounded-t-2xl bg-white shadow-2xl overflow-hidden ${dropdownClassName}`
        : `fixed w-full max-w-[calc(100vw-1rem)] z-[9999] bg-white border rounded-lg shadow-lg overflow-hidden ${dropdownClassName}`}
      style={sheet ? {
        // ยึดขอบล่างของพื้นที่ที่เห็นจริง (เหนือแป้นพิมพ์)
        bottom: vvBox ? Math.max(0, window.innerHeight - vvBox.top - vvBox.height) : 0,
        maxHeight: sheetMax,
        paddingBottom: vvBox && window.innerHeight - vvBox.height > 120 ? 0 : 'env(safe-area-inset-bottom, 0px)',
      } : {
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        maxHeight: maxHeight,
      }}
    >
      {sheet && (
        <div className="flex shrink-0 items-center justify-between border-b px-4">
          <span className="min-w-0 truncate py-2 text-base font-semibold text-gray-800">{title || ariaLabel || (typeof placeholder === 'string' ? placeholder : '')}</span>
          <button type="button" onClick={closeSheet} aria-label={t('ปิด')} className="-mr-3 flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
      {/* Search Input */}
      {searchable && (
        <div className="p-2 border-b shrink-0">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-label={t('พิมพ์เพื่อค้นหา...')}
              className="w-full min-h-[44px] sm:min-h-0 pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base sm:text-sm"
            />
            <svg aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      )}

      {/* Options List */}
      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        className={`w-full overflow-auto overscroll-contain ${sheet ? 'flex-1 min-h-0' : 'max-h-60'}`}
        style={sheet ? undefined : { maxHeight: `min(15rem, ${maxHeight - (searchable ? 60 : 0)}px)` }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600 mr-2"></div>
            <span className="text-gray-500">{loadingMessage}</span>
          </div>
        ) : displayOptions.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {noOptionsMessage}
          </div>
        ) : (
          <>
            {sheet && allLabel && !multiple && !searchTerm && (
              <div
                role="option"
                aria-selected={value == null || value === ''}
                onClick={() => { onChange?.(null); closeSheet(); }}
                className={`flex min-h-[52px] cursor-pointer items-center border-b border-gray-100 px-4 py-2 active:bg-brand-50 ${value == null || value === '' ? 'bg-brand-100 font-semibold text-brand-900' : 'text-gray-600'}`}
              >
                <span className="flex-1">{allLabel}</span>
                {(value == null || value === '') && (
                  <svg className="ml-2 h-5 w-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                )}
              </div>
            )}
            {displayOptions.map((option, index) => {
              const optValue = getValue(option);
              const isSelected = multiple
                ? (value || []).includes(optValue)
                : value === optValue;
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={optValue ?? index}
                  role="option"
                  aria-selected={!!isSelected}
                  className={`
                    flex items-center px-3 py-2 cursor-pointer transition-colors active:bg-brand-50
                    ${sheet ? 'border-b border-gray-100 px-4' : ''}
                    ${isHighlighted ? 'bg-brand-50' : ''}
                    ${isSelected ? 'bg-brand-100 text-brand-900' : 'hover:bg-gray-50'}
                  `}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{ minHeight: sheet ? Math.max(48, itemHeight) : itemHeight }}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="w-5 h-5 mr-3 text-brand-600 rounded border-gray-300"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {renderOption ? (
                      renderOption(option, { isSelected, isHighlighted })
                    ) : (
                      <span className="block truncate">{getLabel(option)}</span>
                    )}
                  </div>
                  {!multiple && isSelected && (
                    <svg className="w-5 h-5 text-brand-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              );
            })}

            {/* Show more indicator */}
            {filteredOptions.length > maxDisplayItems && (
              <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border-t">{t('แสดง {shown} จาก {total} รายการ (พิมพ์เพื่อค้นหาเพิ่มเติม)', { shown: maxDisplayItems, total: filteredOptions.length })}</div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <div
        ref={containerRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        className={`
          flex items-center justify-between w-full min-h-[44px] px-3 py-2 bg-white border rounded-lg cursor-pointer
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-gray-400'}
          ${isOpen ? 'ring-2 ring-brand-500 border-brand-500' : 'border-gray-300'}
          ${error ? 'border-red-500 ring-red-500' : ''}
        `}
      >
        <div className="flex-1 min-w-0 mr-2">
          {renderDisplayValue()}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Clear Button */}
          {clearable && (multiple ? (value || []).length > 0 : value) && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('ล้างค่าที่เลือก')}
              className="-my-2 flex min-h-[44px] min-w-[36px] items-center justify-center text-gray-400 hover:text-gray-600 rounded"
            >
              <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Loading Indicator */}
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-brand-600"></div>
          )}

          {/* Dropdown Arrow */}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

      {/* Hidden Input for Form Submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          id={id}
          value={multiple ? JSON.stringify(value || []) : (value ?? '')}
          required={required}
        />
      )}

      {/* Dropdown Portal */}
      {isOpen && createPortal(sheet ? (
        <>
          <div className="fixed inset-0 z-[9998] bg-black/40" onClick={closeSheet} aria-hidden="true" />
          {dropdownContent}
        </>
      ) : dropdownContent, document.body)}
    </div>
  );
};

export default SmartComboBox;
